import React from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, CartesianGrid } from 'recharts';
import { COLORS } from './constants';
import CustomBar from './CustomBar';
import CustomTooltip from './CustomTooltip';
import { calculateDateStats } from './distributionUtils';
import ExportMenu from './../data/selected_data/export_menu/ExportMenu';

const TypeView = ({
    selectedType,
    subTypeData, // This is the sorted data ready for the chart
    stats,
    rawData,
    substanceTypes, // Needed to find total products for the selected type
    goBackToTypes,
    selectSubType
}) => {

    const typeInfo = substanceTypes.find(t => t.name === selectedType);
    const totalProductsForType = typeInfo ? typeInfo.value : 0;
    const topSubType = subTypeData.length > 0 ? subTypeData[0] : null; // Already sorted
    const percentOfTotal = stats.totalProducts > 0
        ? ((totalProductsForType / stats.totalProducts) * 100).toFixed(1)
        : 0;

    const allProductsForType = typeInfo
        ? Object.values(typeInfo.subTypes || {}).flatMap(sub => sub.products)
        : [];

    const dateStats = calculateDateStats(allProductsForType);
    const AllData = rawData || [];

    // Create a Set of 'pnumber' values from allProductsForType for efficient lookup
    const pnumbersToKeep = new Set(allProductsForType.map(product => product.pnumber));

    // Filter AllData to keep only items where 'eu_pnumber' is in the Set
    const filteredAllData = AllData.filter(item => pnumbersToKeep.has(item.eu_pnumber));

    return (
        <div className="distribution-type-view">
            {/* Sticky Navigation Header */}
            <div className="navigation-header sticky-nav">
                <button className="back-button" onClick={goBackToTypes}>
                    <span className="back-icon">←</span> Back to Overview
                </button>
                <div className="breadcrumb-trail">
                    <span className="breadcrumb-link" onClick={goBackToTypes}>Overview</span>
                    <span className="breadcrumb-separator">›</span>
                    <span className="current-breadcrumb">{selectedType}</span>
                </div>
            </div>
            <div className="content-below-sticky-nav">
                <div className="page-header">
                    <h2>{selectedType} Subtypes</h2>
                    <p className="subtitle">Explore distribution within the {selectedType} category</p>
                </div>
                <div className="type-stats">
                    <div className="stat-card">
                        <div className="stat-icon">💊</div>
                        <h3>{totalProductsForType}</h3>
                        <p>Medicines</p>
                        <span className="sub-stat">{percentOfTotal}% of database</span>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon">🔍</div>
                        <h3>{subTypeData.length}</h3>
                        <p>Subtypes</p>
                    </div>
                    {dateStats && (
                        <div className="stat-card">
                            <div className="stat-icon">📅</div>
                            <h3>{dateStats.rangeText}</h3>
                            <p>Authorisation Span</p>
                            <span className="sub-stat">{dateStats.earliest} - {dateStats.latest}</span>
                        </div>
                    )}
                    {topSubType && (
                        <div className="stat-card highlight-card">
                            <div className="stat-icon">🌟</div>
                            <h3>{topSubType.name}</h3>
                            <p>Top Subtype</p>
                            <span className="sub-stat">{topSubType.value} medicines</span>
                        </div>
                    )}
                </div>

                <div className="stats-summary">
                    <h3>{selectedType} Insights</h3>
                    <p>
                        {selectedType} represents <strong>{percentOfTotal}%</strong> of all medicines in the database
                        with <strong>{totalProductsForType}</strong> total medicines across <strong>{subTypeData.length}</strong> subtypes.
                        {dateStats && ` The authorisation dates span ${dateStats.rangeText}, from ${dateStats.earliest} to ${dateStats.latest}.`}
                        {topSubType && ` The most common subtype is ${topSubType.name} with ${topSubType.value} medicines, representing
                        ${totalProductsForType > 0 ? ((topSubType.value / totalProductsForType) * 100).toFixed(1) : 0}% of all ${selectedType} medicines.`}
                    </p>
                </div>

                {/* Subtypes Distribution Chart */}
                {subTypeData.length > 0 ? (
                    <div className="chart-container interactive-chart">
                        <div className="chart-header">
                            <h3>Subtypes Distribution</h3>
                            <div className="chart-instruction">
                                <span className="pulse-dot"></span>
                                Click on any bar to view medicines
                            </div>
                            <div className="button-container">
                                <ExportMenu selectedData={filteredAllData} text="Export Type" />
                            </div>
                        </div>
                        <ResponsiveContainer width="100%" height={400}>
                            <BarChart
                                data={subTypeData} // Use pre-sorted data
                                margin={{ top: 20, right: 30, left: 20, bottom: 100 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="name" angle={-45} textAnchor="end" interval={0} height={100} />
                                <YAxis />
                                <Tooltip content={<CustomTooltip currentView="type" />} />
                                <Bar dataKey="value" shape={(props) => <CustomBar {...props} onClick={selectSubType} />}>
                                    {subTypeData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[(index + 5) % COLORS.length]} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                ) : (
                    <div className="no-data">
                        <div className="no-data-icon">📭</div>
                        <p>No subtypes available for this substance type.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TypeView;